import ApiResponse from "../models/ApiResponse";
import axios from "./axios-instance";
import FollowUp from "../models/FollowUp";
import {getDateFromString} from "../utils/DateTimeUtils";
import FollowUpSearchParams from "../models/FollowUpSearchParams";

export const getRecentFollowUps = async(userId: number): Promise<ApiResponse<FollowUp[]>> => {
    try {
        const response = await axios.get('/follow-up/recent/' + userId);
        return {
            data: response.data.map((followUp: any) => (mapResponseToFollowUp(followUp))),
            status: response.status
        };
    } catch (err: any) {
        console.error('Failed to get follow-ups:', err.response?.data || err.message)
        return {
            data:  null,
            status: err.status
        };
    }
}

export const getFollowUpsForUser = async (userId: number): Promise<ApiResponse<FollowUp[]>> => {
    try {
        const response = await axios.get('/follow-up/all/user/' + userId);
        return {
            data: response.data.map((followUp: any) => (mapResponseToFollowUp(followUp))),
            status: response.status
        };
    } catch (err: any) {
        console.error('Failed to get follow-ups for user:', err.response?.data || err.message)
        return {
            data:  [],
            status: err.status
        };
    }
}

export const getFollowUpsForClient = async (clientId: number): Promise<ApiResponse<FollowUp[]>> => {
    try {
        const response = await axios.get('/follow-up/all/' + clientId);
        return {
            data: response.data.map((followUp: any) => (mapResponseToFollowUp(followUp))),
            status: response.status
        };
    } catch (err: any) {
        console.error('Failed to get follow-ups for client:', err.response?.data || err.message)
        return {
            data:  [],
            status: err.status
        };
    }
}

export const addFollowUp = async (followUp: FollowUp): Promise<ApiResponse<FollowUp>> => {
    try {
        const response = await axios.post('/follow-up/add', followUp);
        const savedFollowUp = response.data;
        return {
            data: mapResponseToFollowUp(savedFollowUp),
            status: response.status
        }
    } catch (err: any) {
        console.error('Failed to save follow-up: ', err.response?.data || err.message);
        return {
            data: null,
            status: err.status
        }
    }
}

export const updateFollowUp = async (followUp: FollowUp): Promise<ApiResponse<FollowUp>> => {
    try {
        const response = await axios.post('/follow-up/update', followUp);
        const updatedFollowUp = response.data;
        return {
            data: mapResponseToFollowUp(updatedFollowUp),
            status: response.status
        }
    } catch (err: any) {
        console.error('Failed to update follow-up: ', err.response?.data || err.message);
        return {
            data: null,
            status: err.status
        }
    }
}

export const deleteFollowUp = async (id: number): Promise<{status: number}> => {
    try {
        const response = await axios.delete('/follow-up/delete/' + id);
        return {
            status: response.status
        }
    } catch (err: any) {
        console.error('Failed to delete follow-up: ', err.response?.data || err.message);
        return {
            status: err.status
        }
    }
}

export const completeFollowUp = async (id: number): Promise<{status: number}> => {
    try {
        const response = await axios.post('/follow-up/complete/' + id);
        return {
            status: response.status
        }
    } catch (err: any) {
        console.error('Failed to complete follow-up: ', err.response?.data || err.message);
        return {
            status: err.status
        }
    }
}

export const getFollowUpList = async (params: FollowUpSearchParams):
    Promise<ApiResponse<{ followUps: FollowUp[], totalCount: number }>> => {
    try {
        const response = await axios.post('/follow-up/page', params);
        const followUpList = response.data.content.map((followUp: any) => (mapResponseToFollowUp(followUp)));
        return {
            data: {
                followUps: followUpList as FollowUp[],
                totalCount: response.data.totalElements
            },
            status: response.status
        }
    } catch (err: any) {
        console.error('Failed to get follow-ups page: ', err.response?.data || err.message);
        return {
            data: {
                followUps: [],
                totalCount: 0
            },
            status: err.status
        }
    }
}

const mapResponseToFollowUp = (followUp: any): FollowUp => {
    return {
        ...followUp,
        followUpDate: followUp.followUpDate ? getDateFromString(followUp.followUpDate) : null
    }
}